// WHAT: Tab component displaying Parqet import history and error analysis.
// WHY:  Provides interface for reviewing past import runs, analyzing errors by broker, and troubleshooting import issues.
// DOMAIN: Investments → Parqet
// DEPENDENCIES: React, @/components/ui, @/domains/logging/utils/debugLog, @tauri-apps/api/core
// RISKS:
//   - WARN: Tauri commands 'get_import_history', 'get_error_statistics', 'get_import_errors' must exist in backend.
//   - NOTE: Error statistics and import history are loaded separately; may show inconsistent data if one fails.
//   - PERF: Loading errors for selected run may be slow with many errors; consider pagination.

import { invoke } from "@tauri-apps/api/core";
import { type FC, useState, useEffect, useCallback } from "react";

import { Card } from "@/components/ui";
import { debugLog } from "@/domains/logging/utils/debugLog";

/**
 * WHAT:
 *   Formats date string to German locale format with time.
 *
 * WHY:
 *   Provides consistent date/time formatting for import run timestamps.
 *
 * INPUTS:
 *   - dateString: string - Date string to format
 *
 * OUTPUT:
 *   - string - Formatted date/time string
 *
 * RISKS:
 *   - NOTE: Uses 'de-DE' locale; may not match user preferences.
 *   - WARN: Invalid date strings may cause errors.
 *
 * @param dateString - Date string to format
 *
 * @returns {string} Formatted date/time string
 *
 */
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString("de-DE");
};

interface ImportRun {
  directory: string;
  failed: number;
  finished_at: string;
  id: string;
  imported: number;
  parser_version: string;
  skipped: number;
  started_at: string;
  trace_id: string;
}

interface ParserError {
  broker_detected: string;
  created_at: string;
  error_message: string;
  file_name: string;
  file_path: string;
  id: string;
  trace_id: string;
}

interface ErrorStats {
  broker_detected: string;
  error_count: number;
  import_sessions: number;
}

/**
 * WHAT:
 *   Tab component displaying Parqet import history and error analysis.
 *
 * WHY:
 *   Provides users with interface for reviewing past import runs, analyzing errors by broker,
 *   and troubleshooting import issues. Enables identification of common error patterns and
 *   broker-specific problems.
 *
 * BEHAVIOR:
 *   - Loads import history (last 50 runs) and error statistics on mount
 *   - Displays error statistics grouped by broker
 *   - Renders import history table with dates, directories, import counts, and parser versions
 *   - Allows clicking on import runs to view associated errors
 *   - Shows error details (file name, path, error message, broker) for selected run
 *   - Provides refresh functionality to reload data
 *   - Uses debug logging for troubleshooting
 *
 * RISKS:
 *   - WARN: Tauri commands 'get_import_history', 'get_error_statistics', 'get_import_errors' must exist in backend.
 *   - NOTE: Error statistics and import history are loaded separately; may show inconsistent data if one fails.
 *   - PERF: Loading errors for selected run may be slow with many errors; consider pagination.
 *   - NOTE: Error display is limited to selected run; users must click each run to see errors.
 *
 * @returns {JSX.Element} The ImportHistoryTab component
 *
 */
export const ImportHistoryTab: FC = () => {
  /**
   * WHAT:
   *   Effect hook to log component mount for debugging.
   *
   * WHY:
   *   Provides visibility into component lifecycle for troubleshooting.
   *
   * SIDE EFFECTS:
   *   - Logs debug message when component mounts
   */
  useEffect(() => {
    debugLog("info", "investments", "ImportHistoryTab mounted", {
      module: "parqet",
    });
  }, []);

  const [importRuns, setImportRuns] = useState<ImportRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<ImportRun | undefined>();
  const [errors, setErrors] = useState<ParserError[]>([]);
  const [errorStats, setErrorStats] = useState<ErrorStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * WHAT:
   *   Loads import history from backend.
   *
   * WHY:
   *   Retrieves list of past import runs for display in history table.
   *
   * SIDE EFFECTS:
   *   - Sets isLoading to true during fetch
   *   - Invokes 'get_import_history' Tauri command with limit 50
   *   - Updates importRuns state with fetched data
   *   - Logs debug message with count
   *   - Sets isLoading to false on completion
   *
   * RISKS:
   *   - WARN: Tauri command must exist; failures are silently caught.
   *   - NOTE: Limit is hardcoded to 50; may not show all import runs.
   */
  const loadImportHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await invoke<{ runs: ImportRun[] }>("get_import_history", {
        limit: 50,
      });
      setImportRuns(result.runs);
      debugLog("info", "investments", "Loaded import history", {
        count: result.runs.length,
      });
    } catch {
      // NOTE: Failed to load import history - error handled by state
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * WHAT:
   *   Loads error statistics grouped by broker.
   *
   * WHY:
   *   Provides overview of error patterns to identify broker-specific issues.
   *
   * SIDE EFFECTS:
   *   - Invokes 'get_error_statistics' Tauri command
   *   - Updates errorStats state with fetched data
   *
   * RISKS:
   *   - WARN: Tauri command must exist; failures are silently caught.
   *   - NOTE: Error statistics may not reflect latest import runs if not refreshed.
   */
  const loadErrorStats = useCallback(async () => {
    try {
      const result = await invoke<{ stats: ErrorStats[] }>("get_error_statistics");
      setErrorStats(result.stats);
    } catch {
      // NOTE: Failed to load error stats - error handled by state
    }
  }, []);

  /**
   * WHAT:
   *   Effect hook to load import history and error statistics on mount.
   *
   * WHY:
   *   Ensures data is available immediately when component is rendered.
   *
   * SIDE EFFECTS:
   *   - Calls loadImportHistory and loadErrorStats
   *
   * RISKS:
   *   - NOTE: Dependencies on callback functions; ensure they're stable or memoized.
   */
  useEffect(() => {
    void loadImportHistory();
    void loadErrorStats();
  }, [loadImportHistory, loadErrorStats]);

  /**
   * WHAT:
   *   Handles clicking on an import run to load associated errors.
   *
   * WHY:
   *   Allows users to drill down into specific import runs to see what went wrong.
   *
   * INPUTS:
   *   - run: ImportRun - Import run object to load errors for
   *
   * SIDE EFFECTS:
   *   - Sets selectedRun state
   *   - Invokes 'get_import_errors' Tauri command with trace_id
   *   - Updates errors state with fetched error data
   *   - Logs debug message with error count
   *
   * RISKS:
   *   - WARN: Tauri command must exist; failures are silently caught.
   *   - PERF: Loading errors may be slow for runs with many errors.
   *
   * @param run
   *
   */
  const handleRunClick = async (run: ImportRun) => {
    setSelectedRun(run);
    try {
      const result = await invoke<{ errors: ParserError[] }>("get_import_errors", {
        traceId: run.trace_id,
      });
      setErrors(result.errors);
      debugLog("info", "investments", "Loaded errors for run", {
        count: result.errors.length,
        trace_id: run.trace_id,
      });
    } catch {
      // NOTE: Failed to load errors - error handled by state
    }
  };

  return (
    <div className="h-full flex flex-col space-y-6">
      <Card className="bg-slate-700/30 border-slate-600/50" padding="lg" variant="elevated">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <svg className="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6Z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Import History</h1>
              <p className="text-slate-400">Verlauf & Fehleranalyse</p>
            </div>
          </div>
          <button
            className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 disabled:opacity-50 transition"
            disabled={isLoading}
            onClick={() => {
              void loadImportHistory();
            }}
          >
            {isLoading ? "Lädt..." : "🔄 Aktualisieren"}
          </button>
        </div>
      </Card>

      {errorStats.length > 0 && (
        <Card className="bg-slate-700/30 border-slate-600/50" padding="lg" variant="default">
          <h2 className="text-xl font-semibold text-white mb-4">📊 Fehlerstatistik nach Broker</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {errorStats.map((stat) => (
              <div className="bg-slate-600/20 rounded-lg p-4" key={stat.broker_detected}>
                <div className="text-lg font-semibold text-white">
                  {stat.broker_detected || "Unknown"}
                </div>
                <div className="text-sm text-slate-400 mt-1">
                  {stat.error_count} Fehler in {stat.import_sessions} Sessions
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card
        className="bg-slate-700/30 border-slate-600/50 flex-1 overflow-hidden"
        padding="lg"
        variant="default"
      >
        <h2 className="text-xl font-semibold text-white mb-4">📋 Import-Verlauf</h2>
        <div className="overflow-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="bg-slate-600/30 sticky top-0">
              <tr>
                <th className="text-left p-3 text-slate-300">Datum</th>
                <th className="text-left p-3 text-slate-300">Verzeichnis</th>
                <th className="text-center p-3 text-slate-300">Importiert</th>
                <th className="text-center p-3 text-slate-300">Übersprungen</th>
                <th className="text-center p-3 text-slate-300">Fehler</th>
                <th className="text-center p-3 text-slate-300">Version</th>
              </tr>
            </thead>
            <tbody>
              {importRuns.map((run) => (
                <tr
                  className={`cursor-pointer hover:bg-slate-600/20 transition ${
                    selectedRun?.id === run.id ? "bg-purple-500/10" : ""
                  }`}
                  key={run.id}
                  onClick={() => {
                    void handleRunClick(run);
                  }}
                >
                  <td className="p-3 text-slate-300">{formatDate(run.started_at)}</td>
                  <td className="p-3 text-slate-400 text-xs truncate max-w-xs">{run.directory}</td>
                  <td className="p-3 text-center text-green-400">{run.imported}</td>
                  <td className="p-3 text-center text-yellow-400">{run.skipped}</td>
                  <td className="p-3 text-center text-red-400">{run.failed}</td>
                  <td className="p-3 text-center text-slate-400">{run.parser_version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedRun && errors.length > 0 ? (
        <Card className="bg-red-500/10 border-red-500/30" padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-white mb-4">
            ❌ Fehler für Session: {selectedRun.trace_id.slice(0, 8)}
          </h3>
          <div className="space-y-2 max-h-64 overflow-auto">
            {errors.map((error) => (
              <div className="bg-slate-600/20 rounded p-3" key={error.id}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-white">{error.file_name}</div>
                    <div className="text-xs text-slate-400 mt-1">{error.file_path}</div>
                    <div className="text-sm text-red-400 mt-2">{error.error_message}</div>
                  </div>
                  <div className="text-xs text-slate-500 ml-4">
                    {error.broker_detected || "Unknown"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
};
